import { isDefined } from "@clipboard-health/util-ts";
import { Stack, Theme } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { Text, TextSkeleton, Title } from "@src/appV2/redesign/system/ui-components";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { isPast, minutesToMilliseconds, parseISO } from "date-fns";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { WorkTogetherStackContainer } from "../../WorkWithFriends/components/WorkTogetherStackContainer";
import { ShortLeadTimePill } from "../ArrivalTime/Pill";
import { ShiftForYouPill } from "../ForYou/Pill";
import { getGradientByTimeSlot } from "../getGradientByTimeSlot";
import { type ShiftSlotCoalesced } from "../Open/useGetShiftsSlotV2";
import { TimeSlotPill } from "../TimeSlotPill";
import { ShiftBottomSheetHeaderPill } from "./Pills";

interface ShiftBottomSheetHeaderProps {
  onClose: () => void;
  hideRightAction?: boolean;
  hideBackButton?: boolean;
  shiftSlots?: ShiftSlotCoalesced;
  isShortLeadTime?: boolean;
}

export function ShiftBottomSheetHeader(props: ShiftBottomSheetHeaderProps) {
  const { onClose, shiftSlots, hideRightAction, hideBackButton, isShortLeadTime } = props;

  // Refresh this component every minute to get the latest priority status
  useCurrentDate(minutesToMilliseconds(1));

  const {
    shift,
    resolvedShiftQualification,
    isLoadingShiftModalsData,
    shiftFailedLoading,
    isForYouShift,
  } = useShiftModalsDataContext();
  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([resolvedShiftQualification ?? ""]);
  const [qualificationText] = qualificationDisplayLabels;

  const showLoadingState = !isDefined(shift) && isLoadingShiftModalsData && !shiftFailedLoading;

  const isSingleOrientationShift = shift?.attributes.orientationShiftType === "single-shift";

  function getBackground(theme: Theme) {
    if (isRebrandingEnabled()) {
      return theme.palette.background.secondary;
    }

    if (isSingleOrientationShift) {
      return theme.palette.intent?.success.gradient;
    }

    if (isDefined(shift?.attributes.timeSlot)) {
      return getGradientByTimeSlot({ timeSlot: shift.attributes.timeSlot, theme });
    }

    return undefined;
  }

  return (
    <Stack
      alignItems="space-between"
      sx={(theme) => ({
        paddingTop: 6,
        paddingBottom: 7,
        px: 5,
        transition: theme.transitions.create(["background"]),
        background: getBackground(theme),
      })}
      spacing={4}
    >
      <PageHeaderTopRow
        sx={{ px: 0, paddingBottom: 3 }}
        hideBackButton={hideBackButton}
        rightAction={
          isDefined(shift) &&
          !hideRightAction && (
            <Stack direction="row" spacing={2} alignItems="center">
              {isRebrandingEnabled() && isDefined(shift.attributes.timeSlot) && (
                <>
                  {isShortLeadTime ? (
                    <ShortLeadTimePill shouldLeaveNow={isPast(parseISO(shift.attributes.start))} />
                  ) : (
                    <TimeSlotPill timeSlot={shift.attributes.timeSlot} />
                  )}
                  {isForYouShift && <ShiftForYouPill />}
                </>
              )}
              <ShiftBottomSheetHeaderPill
                shiftType={shift.attributes.type}
                hasShiftAuction={isDefined(shift.shiftAuction)}
              />
            </Stack>
          )
        }
        onClickBack={() => {
          logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOSED, { shiftId: shift?.id ?? "" });

          if (shiftFailedLoading) {
            logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_UNAVAILABLE_CLOSED, {
              shiftId: shift?.id ?? "",
            });
          }

          onClose();
        }}
      />

      <Stack sx={{ paddingLeft: 3 }} spacing={4}>
        {showLoadingState || isLoadingQualification ? (
          <TextSkeleton variant="body1" width="20%" />
        ) : (
          <Text semibold variant="body1">
            {qualificationText}
          </Text>
        )}

        {showLoadingState && <TextSkeleton variant="h3" width="80%" />}

        <Stack direction="row" spacing={1} justifyContent="space-between">
          {!showLoadingState && isDefined(shift?.attributes.start) && (
            <Title variant="h3" component="h2">
              {formatDayOfMonthWithDayName(parseISO(shift.attributes.start), {
                formatDayString: "EEEE",
                timezone: shift.workplace.attributes.location.timezone,
              })}
            </Title>
          )}

          {isDefined(shiftSlots?.shiftAssignments) && isDefined(shift?.id) && (
            <WorkTogetherStackContainer
              workers={shiftSlots.shiftAssignments
                .filter((assignment) => assignment.shiftId !== shift.id)
                .map(({ worker }) => worker)}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
